import React from 'react';
import { Link } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';
import usePageMeta from '../shared/hooks/usePageMeta';

function TermsOfService() {
  usePageMeta('Terms of Service', 'Terms of Service for NextAInsight, an AI-powered social media content automation platform by hellAIdea Ltd.');

  return (
    <div className="container mx-auto px-4 py-16 max-w-4xl selection:bg-slate-blue/30 selection:text-cream">
        <div className="bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border border-white/5 p-8 md:p-16 relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-black text-cream mb-4 tracking-tight">Terms of Service</h1>
            <p className="text-sm font-bold text-slate-blue uppercase tracking-widest mb-12 border-b border-white/10 pb-6 inline-block">Last Updated: February 2026</p>

            <div className="space-y-10 text-cream/80 leading-relaxed text-lg">

              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> About NextAInsight</h2>
                <p>
                  NextAInsight is an AI-powered social media content automation platform operated by{' '}
                  <strong className="text-white">hellAIdea Ltd</strong>, a company registered in Scotland, United Kingdom.
                  The service enables you to create AI-generated content (text and images), schedule posts,
                  and publish directly to your Facebook Pages and Instagram accounts via the Meta API.
                </p>
                <p className="mt-3">
                  By using NextAInsight, you agree to these Terms of Service. If you do not agree, please do not use the service.
                </p>
              </section>

              {/* Nature of the Service */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Nature of the Service</h2>
                <div className="p-4 bg-slate-blue/10 border border-slate-blue/20 rounded-xl">
                  <p>
                    <strong className="text-white">NextAInsight is a private tool operated by hellAIdea Ltd.</strong> This means:
                  </p>
                  <ul className="list-none mt-3 space-y-2 ml-2">
                    <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Features may change, be added, or be removed without prior notice.</li>
                    <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> The service may experience downtime or unexpected behaviour.</li>
                    <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Accounts are created by hellAIdea Ltd and access may be revoked at our discretion.</li>
                  </ul>
                </div>
              </section>

              {/* Eligibility */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Account Eligibility</h2>
                <p>To use NextAInsight, you must:</p>
                <ul className="list-none mt-4 space-y-3 ml-2">
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">✓</span> Be at least 18 years old.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">✓</span> Have been given an account by hellAIdea Ltd.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">✓</span> Provide accurate and complete information.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">✓</span> Be authorised to manage any Facebook Pages or Instagram accounts you connect.</li>
                </ul>
                <p className="mt-3">
                  You are responsible for maintaining the security of your account credentials. You must not share your account with others.
                </p>
              </section>

              {/* User Responsibilities */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Your Responsibilities</h2>
                <p>
                  NextAInsight provides tools to help you create and schedule social media content.{' '}
                  <strong className="text-white">You are the publisher</strong> and are solely responsible for all content
                  posted to your connected accounts, whether created manually, generated by AI, or posted automatically via recurring streams.
                </p>
                <p className="mt-3">You are responsible for:</p>
                <ul className="list-none mt-3 space-y-2 ml-2">
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Reviewing all content before publication (or accepting responsibility for automated posts).</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Ensuring content complies with applicable laws and platform policies.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Having the rights to any content you input (brand descriptions, images, reference materials).</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Monitoring your connected social media accounts for any issues.</li>
                </ul>
              </section>

              {/* Content Ownership */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Content Ownership</h2>
                <p>
                  You retain all rights to the content you create or input into NextAInsight. By using the service,
                  you grant us a limited, non-exclusive licence to process, store, and publish your content to your
                  connected social media accounts <strong className="text-white">as instructed by you</strong>.
                  This licence ends when you delete the content or your account.
                </p>
              </section>

              {/* AI Content */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> AI-Generated Content</h2>
                <p>
                  Content created through NextAInsight may be generated with the assistance of artificial intelligence.{' '}
                  <strong className="text-white">By using our service, you acknowledge and agree that:</strong>
                </p>
                <ul className="list-none mt-4 space-y-3 ml-2">
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">▹</span> You understand that content is generated using AI and you are responsible for any required disclosures in your jurisdiction or platform.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">▹</span> You will review all content before publishing to ensure accuracy and appropriateness.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">▹</span> You are responsible for compliance with platform policies regarding AI content.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">▹</span> For Recurring Streams: content is automatically generated and posted on your behalf, and you take full responsibility for all automated posts.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">▹</span> AI-generated content may occasionally be inaccurate, inappropriate, or not reflect your intended message.</li>
                </ul>
                <div className="mt-4 p-4 bg-slate-blue/10 border border-slate-blue/20 rounded-xl text-sm italic text-slate-blue/80">
                  You may optionally add an AI disclosure signature to your posts using the toggle in the scheduling options.
                </div>
              </section>

              {/* Acceptable Use */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Acceptable Use</h2>
                <p>You agree <strong className="text-white">NOT</strong> to use NextAInsight for:</p>
                <ul className="list-none mt-4 space-y-3 ml-2">
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Spam, unsolicited bulk messaging, or automated engagement manipulation.</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Harassment, hate speech, threats, or harmful content.</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Violations of Meta's Community Standards, Terms of Service, or Platform Policies.</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Copyright or intellectual property infringement.</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Distributing malware, phishing, or deceptive content.</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Any illegal activities under UK or applicable law.</li>
                </ul>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Limitation of Liability</h2>
                <p>
                  The service is provided <strong className="text-white">"as is"</strong> and{' '}
                  <strong className="text-white">"as available"</strong> without warranties of any kind, whether
                  express or implied, including but not limited to implied warranties of merchantability, fitness for
                  a particular purpose, and non-infringement.
                </p>
                <p className="mt-3">To the fullest extent permitted by law, hellAIdea Ltd shall not be liable for:</p>
                <ul className="list-none mt-3 space-y-2 ml-2">
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Any issues with your Meta (Facebook/Instagram) accounts, including account restrictions, content removal, or policy violations.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Loss of data, revenue, or business opportunities arising from use of the service.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Inaccuracies or unintended consequences of AI-generated content.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Service interruptions, downtime, or data loss.</li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> Actions taken by Meta or any third-party platform in response to content posted via the service.</li>
                </ul>
                <p className="mt-3">
                  Nothing in these terms excludes or limits our liability for death, personal injury caused by negligence,
                  fraud, or any other liability that cannot be excluded by law.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Termination</h2>
                <p>
                  We reserve the right to suspend or terminate your account at any time if you violate these terms
                  or engage in misuse of the service.
                </p>
                <p className="mt-3">
                  You may request account deletion at any time by contacting us at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-blue font-bold hover:text-white transition-colors hover:underline">{SUPPORT_EMAIL}</a>.
                  Upon deletion, we will remove your personal data in accordance with our{' '}
                  <Link to="/privacy-policy" className="text-slate-blue font-bold hover:text-white transition-colors hover:underline">Privacy Policy</Link>.
                </p>
              </section>

              {/* Changes to Terms */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Changes to These Terms</h2>
                <p>
                  We may update these Terms of Service from time to time. We will notify you of significant changes
                  by email or through a notice in the app. Continued use of the service after changes take effect
                  constitutes acceptance of the revised terms.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Governing Law and Disputes</h2>
                <p>
                  These Terms of Service are governed by the laws of <strong className="text-white">Scotland, United Kingdom</strong>.
                  Any disputes arising from or in connection with these terms shall be subject to the exclusive
                  jurisdiction of the Scottish courts.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Contact Us</h2>
                <p>
                  Questions about these terms? Contact us at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-blue font-bold hover:text-white transition-colors hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </section>
            </div>

          </div>
        </div>
      </div>
  );
}

export default TermsOfService;
